import { z } from 'zod';

/**
 * Tenant phone-number provisioning contracts (`/api/admin/tenants/:id/numbers`).
 * Single source of truth shared by the Worker validators and the SPA client
 * per ARCHITECTURE_PLAN §3.
 *
 * Why this exists: `phone_numbers.e164` is the routing key that maps an inbound
 * Twilio webhook to a tenant (`src/webhooks/twilio.ts`) and the tenant key the
 * voice agent's tools resolve against (`src/api/voiceTools.ts`). Until this
 * surface existed nothing in the codebase ever INSERTed that row, so a tenant
 * created through the admin wizard silently dropped every inbound call and SMS
 * — the webhook was marked `ignored` and answered 200.
 *
 * v1 scope is ATTACH, not purchase: the operator buys the number in the Twilio
 * console and records it here. Twilio number search/purchase is deliberately
 * out of scope — it spends money, and the attach path is what unblocks routing.
 * The webhook URLs, however, ARE configured through Twilio's API, because a
 * number whose webhooks point elsewhere is just as dead as a missing row.
 */

/** E.164: leading `+`, country digit 1-9, up to 15 digits total. */
export const E164Schema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{1,14}$/, 'must be E.164, e.g. +15551234567');

/**
 * Twilio IncomingPhoneNumber SID. `PN` + 32 hex characters — validated here so
 * an obviously-wrong SID fails at the edge instead of as a Twilio 404.
 */
export const PhoneNumberSidSchema = z
  .string()
  .trim()
  .regex(/^PN[0-9a-fA-F]{32}$/, 'must be a Twilio IncomingPhoneNumber SID (PN…)');

export const PhoneNumberStatusSchema = z.enum(['active', 'released']);
export type PhoneNumberStatus = z.infer<typeof PhoneNumberStatusSchema>;

/** Capabilities as stored in `phone_numbers.capabilities_json`. */
export const PhoneNumberCapabilitiesSchema = z.object({
  voice: z.boolean(),
  sms: z.boolean(),
});
export type PhoneNumberCapabilities = z.infer<typeof PhoneNumberCapabilitiesSchema>;

/** One `phone_numbers` row as exposed to the platform admin. */
export const AdminPhoneNumberSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  e164: z.string(),
  provider: z.string(),
  providerSid: z.string(),
  capabilities: PhoneNumberCapabilitiesSchema,
  voiceAssistantId: z.string().nullable(),
  status: PhoneNumberStatusSchema,
  webhookStatus: z.enum(['unknown', 'configured', 'failed']),
  webhookError: z.string().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type AdminPhoneNumber = z.infer<typeof AdminPhoneNumberSchema>;

/** `GET /api/admin/tenants/:id/numbers` response. */
export const AdminPhoneNumbersResponseSchema = z.object({
  numbers: z.array(AdminPhoneNumberSchema),
});
export type AdminPhoneNumbersResponse = z.infer<typeof AdminPhoneNumbersResponseSchema>;

/**
 * `POST /api/admin/tenants/:id/numbers` body — attach a number the operator
 * already owns in Twilio.
 *
 * `configureWebhooks` drives a real Twilio API write that points the number's
 * Voice and SMS URLs at this deployment's `PUBLIC_BASE_URL`. It defaults to
 * true because the overwhelmingly common failure is a number that exists in
 * both systems but still delivers its webhooks somewhere else.
 */
export const AdminAttachPhoneNumberBodySchema = z.object({
  e164: E164Schema,
  providerSid: PhoneNumberSidSchema,
  capabilities: PhoneNumberCapabilitiesSchema.default({ voice: true, sms: true }),
  configureWebhooks: z.boolean().default(true),
});
export type AdminAttachPhoneNumberBody = z.infer<typeof AdminAttachPhoneNumberBodySchema>;

/**
 * `POST /api/admin/tenants/:id/numbers` response.
 *
 * `webhooksConfigured` is reported rather than assumed: the row is written and
 * the webhook call is made in that order, so the caller must be able to tell
 * "routed and configured" from "routed, but Twilio still points elsewhere".
 * A webhook failure surfaces as an error — it is never silently swallowed.
 */
export const AdminAttachPhoneNumberResponseSchema = z.object({
  number: AdminPhoneNumberSchema,
  webhooksConfigured: z.boolean(),
});
export type AdminAttachPhoneNumberResponse = z.infer<typeof AdminAttachPhoneNumberResponseSchema>;

/**
 * `DELETE /api/admin/tenants/:id/numbers/:numberId` response.
 *
 * Release is a status flip, never a DELETE: `calls.phone_number_id` and
 * `conversations.phone_number_id` are NOT NULL foreign keys, so removing the
 * row would orphan every historical call and message.
 */
export const AdminReleasePhoneNumberResponseSchema = z.object({
  number: AdminPhoneNumberSchema,
});
export type AdminReleasePhoneNumberResponse = z.infer<typeof AdminReleasePhoneNumberResponseSchema>;
